/**
 * SmartFeedbackPrompt — intelligent star-rating prompt shown ONCE per
 * session to eligible beta users. Eligibility is decided server-side
 * (>=3 logins OR >=3 sections visited OR >=5 min session time, AND no
 * dismissal in last 7 days, AND no submission in last 30 days).
 *
 * UX: small modal with 5 stars. Tapping a star opens the main
 * BetaFeedbackModal pre-filled with the rating. "Maybe Later" stamps a
 * 7-day server-side cooldown.
 *
 * Mount: rendered globally inside the auth layout once. Polls
 * eligibility once on mount; sessionStorage flag prevents it from
 * showing more than once per browser session.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Star } from 'lucide-react';
import { useBetaFeedback } from '@/contexts/BetaFeedbackContext';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SURFACE = 'smart_prompt';
const RATING_SURFACE = 'rating_prompt';
const SESSION_FLAG = 'hl_smart_prompt_shown_this_session';

const SmartFeedbackPrompt = () => {
  const { user } = useAuth();
  const { openFeedbackModal, trackEvent } = useBetaFeedback();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(0);
  const [picked, setPicked] = useState(0);

  // Eligibility check + once-per-session guard. Runs only when the
  // user is authenticated and we haven't already shown the prompt
  // since the tab was opened.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const shownThisSession = (() => {
      try { return sessionStorage.getItem(SESSION_FLAG) === '1'; }
      catch { return false; }
    })();
    if (shownThisSession) return;

    // Give the user a few seconds to settle before popping a prompt —
    // never interrupt the first 8 seconds after page load.
    const t = setTimeout(async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/feedback/prompt-eligible`, { withCredentials: true });
        if (cancelled) return;
        if (res.data?.eligible) {
          setOpen(true);
          trackEvent('impression', SURFACE);
          try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch { /* noop */ }
        }
      } catch {
        /* silent — never block UX on a tracking failure */
      }
    }, 8000);

    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleStarClick = (n) => {
    setPicked(n);
    // Log the rating impression as a rating_prompt event so the admin
    // analytics page can build a rating distribution histogram.
    trackEvent('click', RATING_SURFACE, { rating: n });
    setOpen(false);
    // Briefly delay so the dialog fade-out doesn't fight the new modal.
    setTimeout(() => {
      openFeedbackModal(SURFACE, { rating: n });
    }, 150);
  };

  const handleMaybeLater = async () => {
    trackEvent('dismiss', SURFACE);
    setOpen(false);
    try {
      await axios.post(`${API}/feedback/prompt-dismiss`, {}, { withCredentials: true });
    } catch { /* silent */ }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleMaybeLater(); }}>
      <DialogContent className="sm:max-w-sm" data-testid="smart-feedback-prompt">
        <DialogHeader>
          <DialogTitle className="text-base text-center">
            How is your experience with HealthLoop so far?
          </DialogTitle>
          <DialogDescription className="text-xs text-center text-gray-500">
            One tap. We use this to prioritise what we build next.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-1.5 py-2" data-testid="smart-prompt-stars">
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = n <= (hovered || picked);
            return (
              <button
                key={n}
                type="button"
                aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
                className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 rounded-md"
                onClick={() => handleStarClick(n)}
                onMouseEnter={() => setHovered(n)}
                onMouseLeave={() => setHovered(0)}
                data-testid={`smart-prompt-star-${n}`}
              >
                <Star className={`h-8 w-8 ${filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMaybeLater}
            className="text-gray-500 hover:text-gray-700"
            data-testid="smart-prompt-maybe-later-btn"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartFeedbackPrompt;
